import React, { useState } from 'react';
import { membershipTiers, enrollmentSteps, faqs } from '../data/mock';
import { Check, ChevronDown, ChevronRight } from 'lucide-react';

const Membership = () => {
  const [openFaq, setOpenFaq] = useState(null);

  return (
    <section id="membership" className="section-padding bg-[#fffef8]">
      <div className="container-custom">
        {/* Section header */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <div className="gold-accent mx-auto mb-6"></div>
          <h2 className="font-serif text-3xl md:text-4xl text-[#2d2d2d] mb-6">
            Become a Member
          </h2>
          <p className="text-[#5a5a5a] text-lg leading-relaxed">
            Choose the membership tier that aligns with your healthcare needs and 
            lifestyle. Each tier provides exceptional care with varying levels of 
            access and services.
          </p>
        </div>

        {/* Pricing cards */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-24">
          {membershipTiers.map((tier) => (
            <div
              key={tier.id}
              className={`relative bg-[#fffef8] p-6 border ${
                tier.popular
                  ? 'border-[#c9a962] shadow-lg'
                  : 'border-[#e8e7e2]'
              } card-hover`}
            >
              {tier.popular && (
                <div className="absolute -top-4 left-1/2 -translate-x-1/2 bg-[#c9a962] text-[#fffef8] text-xs tracking-wider uppercase px-4 py-1">
                  Most Popular
                </div>
              )}
              
              <h3 className="font-serif text-xl text-[#2d2d2d] mb-2">
                {tier.name}
              </h3>
              <p className="text-[#8a8a8a] text-sm mb-4 min-h-[40px]">{tier.description}</p>
              
              <div className="mb-6">
                <span className="font-serif text-3xl text-[#2d2d2d]">${tier.price}</span>
                <span className="text-[#8a8a8a] text-sm"> {tier.period}</span>
              </div>
              
              <ul className="space-y-3 mb-6">
                {tier.features.map((feature, index) => (
                  <li key={index} className="flex items-start gap-2">
                    <Check size={16} className="text-[#c9a962] flex-shrink-0 mt-0.5" />
                    <span className="text-[#5a5a5a] text-sm">{feature}</span>
                  </li>
                ))}
              </ul>
              
              <a
                href="#contact"
                className={`w-full ${
                  tier.popular ? 'btn-gold' : 'btn-primary'
                } text-sm py-3`}
              >
                Get Started
                <ChevronRight size={14} />
              </a>
            </div>
          ))}
        </div>

        {/* How it works */}
        <div className="mb-24">
          <div className="text-center max-w-2xl mx-auto mb-12">
            <h3 className="font-serif text-2xl md:text-3xl text-[#2d2d2d] mb-4">
              How Concierge Medicine Works
            </h3>
            <p className="text-[#8a8a8a]">
              From your first consultation to ongoing care, here's what to expect.
            </p>
          </div>
          
          <div className="grid md:grid-cols-4 gap-8">
            {enrollmentSteps.map((step, index) => (
              <div key={step.step} className="relative">
                <div className="flex items-center gap-4 mb-4">
                  <span className="font-serif text-4xl text-[#c9a962]">
                    {String(step.step).padStart(2, '0')}
                  </span>
                  {index < enrollmentSteps.length - 1 && (
                    <div className="hidden md:block flex-1 h-px bg-[#e8e7e2]"></div>
                  )}
                </div>
                <h4 className="font-medium text-[#2d2d2d] mb-2">{step.title}</h4>
                <p className="text-sm text-[#8a8a8a] leading-relaxed">
                  {step.description}
                </p>
              </div>
            ))}
          </div>
        </div>

        {/* FAQs */}
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-12">
            <h3 className="font-serif text-2xl md:text-3xl text-[#2d2d2d] mb-4">
              Frequently Asked Questions
            </h3>
          </div>
          
          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <div
                key={index}
                className="border border-[#e8e7e2] bg-[#fffef8]"
              >
                <button
                  className="w-full flex items-center justify-between p-6 text-left"
                  onClick={() => setOpenFaq(openFaq === index ? null : index)}
                >
                  <span className="font-medium text-[#2d2d2d] pr-4">
                    {faq.question}
                  </span>
                  <ChevronDown
                    size={20}
                    className={`text-[#c9a962] flex-shrink-0 transition-transform duration-300 ${
                      openFaq === index ? 'rotate-180' : ''
                    }`}
                  />
                </button>
                {openFaq === index && (
                  <div className="px-6 pb-6 animate-fade-in">
                    <p className="text-[#5a5a5a] leading-relaxed">
                      {faq.answer}
                    </p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Membership;
